import React, { useEffect, useState } from 'react'
import { Spinner } from '@dostavkee/web-ui'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import minMax from 'dayjs/plugin/minMax'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import ReactDOM from 'react-dom/client'
import { Toaster } from 'sonner'

import 'dayjs/locale/ru'
import '@/shared/styles/global.scss'

import { useAuthProfileSetter, useAuthProfileValue, useAuthTokenValue } from '@/entities/auth'
import {
    AXIOS_INSTANCE,
    type DomainUserRoleString,
    useGetUsersProfile,
    useRefreshTokenInterceptor,
} from '@/shared/api'
import { NetworkStatus } from '@/shared/ui/network-status'
import { TechnicalPauseAlert } from '@/shared/ui/technical-pause-alert'

// Import the generated route tree
import { routeTree } from './app/route-tree.generated'
import type { MyRouterContext } from './app/routes/__root.tsx'
import * as TanstackQuery from './app/tanstack-query/root-provider.tsx'
import { useInitWebimVisitor } from './app/webim/use-init-webim-visitor'
/**
 * Устанавливаем глобальные плагины для dayjs
 */
dayjs.extend(customParseFormat)
dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(minMax)
dayjs.locale('ru')
dayjs.extend(relativeTime)
/**
 * Устанавливаем базовый URL для axios перед тем, как приложение будет инициализировано
 */
AXIOS_INSTANCE.defaults.baseURL = __GLOBAL__.VITE_API_URL
AXIOS_INSTANCE.defaults.timeout = 10_000

// Create a new router instance s
const router = createRouter({
    routeTree,
    context: {
        ...TanstackQuery.getContext(),
        auth: {
            profile: undefined!,
            hasAnyRole: () => false,
        },
    } as MyRouterContext,
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultStructuralSharing: true,
    defaultPreloadStaleTime: 0,
    notFoundMode: 'root',
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router
    }
}

// Render the app
const rootElement = document.querySelector('#app')
if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement)
    root.render(<Main />)
}

function Main() {
    return (
        <TanstackQuery.Provider>
            <AuthInterceptor>
                <SetupRouter />
            </AuthInterceptor>
            <NetworkStatus />
            <SetupToast />
            <TechnicalPauseAlert />
            <SetupWebimVisitor />
        </TanstackQuery.Provider>
    )
}

// TODO: Move to another file
function AuthInterceptor({ children }: { children: React.ReactNode }) {
    const { token } = useAuthTokenValue()
    const [isInitialized, setIsInitialized] = useState(false)

    useRefreshTokenInterceptor()

    useEffect(() => {
        if (!token) {
            setIsInitialized(true)

            return
        }

        AXIOS_INSTANCE.defaults.headers.Authorization = `Bearer ${token}`
        setIsInitialized(true)
    }, [token])

    if (!isInitialized) {
        return null
    }

    return children
}

// TODO: Move to another file
function SetupRouter() {
    const { token } = useAuthTokenValue()
    const { profile } = useAuthProfileValue()
    const { profileSetter } = useAuthProfileSetter()
    const { data } = useGetUsersProfile({
        query: {
            refetchOnWindowFocus: 'always',
            enabled: Boolean(token) && !profile,
            retry: 1,
        },
    })

    useEffect(() => {
        if (data?.user && !profile) {
            profileSetter(data.user)
        }
    }, [token, data?.user, profileSetter, profile])

    if (token && !profile) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100dvh',
                }}
            >
                <Spinner />
            </div>
        )
    }

    const hasAnyRole = (roles: DomainUserRoleString[]) => {
        if (!profile?.role) {
            return false
        }

        return roles.includes(profile.role)
    }

    return (
        <RouterProvider
            router={router}
            context={{
                auth: {
                    profile,
                    hasAnyRole,
                },
            }}
        />
    )
}

// TODO: Move to another file
function SetupWebimVisitor() {
    useInitWebimVisitor()

    return null
}

// TODO: Move to another file
function SetupToast() {
    return (
        <Toaster
            closeButton
            richColors
            className={'toaster'}
            gap={8}
            position='top-center'
            swipeDirections={['top']}
            visibleToasts={6}
            toastOptions={{
                style: {
                    borderRadius: '16px',
                },
            }}
        />
    )
}
